import type { RecyclerAppetiteFulfillmentLine } from '../types/recycler-appetite.type';

export interface FulfillmentGroup {
  /** Legacy category code (`plastic`, `cardboard`, ...) used for labels and chips. */
  category: string;
  /** Catalog category code when the backend resolved one. */
  categoryCode: string | null;
  lines: RecyclerAppetiteFulfillmentLine[];
  targetKg: number;
  receivedKg: number;
  /** Rounded percent of target received; null when the group has no target. */
  percent: number | null;
  overTarget: boolean;
}

export interface FulfillmentGroups {
  /** One group per category that has at least one target line, largest target first. */
  targeted: FulfillmentGroup[];
  /** Mass received for waste types with no target line, or null when there is none. */
  untargeted: FulfillmentGroup | null;
}

export function isUntargetedLine(line: RecyclerAppetiteFulfillmentLine): boolean {
  return line.has_target === false;
}

export function lineDisplayName(line: RecyclerAppetiteFulfillmentLine): string {
  return line.material_type_name?.trim() || line.material_id;
}

export function percentOf(receivedKg: number, targetKg: number): number | null {
  if (targetKg <= 0) return null;
  return Math.round((receivedKg / targetKg) * 100);
}

function sumLines(lines: RecyclerAppetiteFulfillmentLine[]): { targetKg: number; receivedKg: number } {
  return lines.reduce(
    (acc, line) => ({ targetKg: acc.targetKg + line.target_kg, receivedKg: acc.receivedKg + line.received_kg }),
    { targetKg: 0, receivedKg: 0 },
  );
}

function byTargetThenName(a: RecyclerAppetiteFulfillmentLine, b: RecyclerAppetiteFulfillmentLine): number {
  if (b.target_kg !== a.target_kg) return b.target_kg - a.target_kg;
  if (b.received_kg !== a.received_kg) return b.received_kg - a.received_kg;
  return lineDisplayName(a).localeCompare(lineDisplayName(b));
}

function buildGroup(category: string, lines: RecyclerAppetiteFulfillmentLine[]): FulfillmentGroup {
  const sorted = [...lines].sort(byTargetThenName);
  const { targetKg, receivedKg } = sumLines(sorted);
  return {
    category,
    categoryCode: sorted.find((line) => line.category_code)?.category_code ?? null,
    lines: sorted,
    targetKg,
    receivedKg,
    percent: percentOf(receivedKg, targetKg),
    overTarget: targetKg > 0 && receivedKg > targetKg,
  };
}

/** Groups fulfillment lines by category with a subtotal per group, so the
 *  dashboard can show every waste type under its category instead of one
 *  indistinguishable category chip per line. */
export function groupFulfillmentLines(lines: RecyclerAppetiteFulfillmentLine[]): FulfillmentGroups {
  const targetedByCategory = new Map<string, RecyclerAppetiteFulfillmentLine[]>();
  const untargetedLines: RecyclerAppetiteFulfillmentLine[] = [];

  lines.forEach((line) => {
    if (isUntargetedLine(line)) {
      untargetedLines.push(line);
      return;
    }
    const key = line.parent_category || 'other';
    if (!targetedByCategory.has(key)) targetedByCategory.set(key, []);
    targetedByCategory.get(key)!.push(line);
  });

  const targeted = Array.from(targetedByCategory.entries())
    .map(([category, groupLines]) => buildGroup(category, groupLines))
    .sort((a, b) => {
      if (b.targetKg !== a.targetKg) return b.targetKg - a.targetKg;
      return a.category.localeCompare(b.category);
    });

  const untargeted = untargetedLines.length > 0 ? buildGroup('untargeted', untargetedLines) : null;
  if (untargeted) {
    // Received mass sorts first within the untargeted group; targets are all zero.
    untargeted.lines.sort((a, b) => b.received_kg - a.received_kg || lineDisplayName(a).localeCompare(lineDisplayName(b)));
  }

  return { targeted, untargeted };
}

export function sumTargeted(groups: FulfillmentGroups): { targetKg: number; receivedKg: number } {
  return groups.targeted.reduce(
    (acc, group) => ({ targetKg: acc.targetKg + group.targetKg, receivedKg: acc.receivedKg + group.receivedKg }),
    { targetKg: 0, receivedKg: 0 },
  );
}
